import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { QUERY_KEYS } from 'constants/constants'
import { Fragment } from 'react'
import trackEvents from '../../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePartnerContext
} from '../../../context'
import { trackEvent } from '../../../helpers/analytics'
import { useMutationAndUpdateQuery } from '../../../hooks/use-mutation-and-update-query'
import Button from '../../tailwind/Button'
import Toggle from '../../tailwind/toggle'
import ReadOnlyFieldset from './read-only-fieldset'

const I18N_PREFIX = 'components.settings.result_reports.'

const Divider = () => <div className='h-px w-full bg-gray-200' />

// readOnly gilt in enforced ohne CUSTOM_REPORTS, wenn schon Einstellungen
// gespeichert sind. Sie bleiben aktiv und lesbar, der Inhalt liegt in einem
// disabled fieldset und Speichern fehlt. Erlaubt ist nur Auf Standard
// zurücksetzen.
const SettingsV2 = ({ readOnly }) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { success, error, alert } = useNotificationContext()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()

  const report_v2_settings = JSON.parse(
    partner.app_settings?.report_v2_settings ?? '{}'
  )

  const { mutate } = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const dimensions_v2 = Object.entries(
    JSON.parse(system.dimensions_pdf_reports.data)
  ).map(([key, value]) => ({ key, ...value }))

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      score_visible: report_v2_settings.score_visible ?? true,
      attachment_visible: report_v2_settings.attachment_visible ?? true,
      culture_visible: report_v2_settings.culture_visible ?? true,
      interests_visible: report_v2_settings.interests_visible ?? true,
      hidden_dimensions: report_v2_settings.hidden_dimensions ?? []
    },
    onSubmit: (
      {
        score_visible,
        attachment_visible,
        culture_visible,
        interests_visible,
        hidden_dimensions
      },
      { setSubmitting }
    ) => {
      setSubmitting(true)
      trackEvent(trackEvents.UPDATE_RESULT_REPORTS_SETTINGS_V2)

      const app_settings = { ...(partner.app_settings || {}) }
      app_settings.report_v2_settings = JSON.stringify({
        score_visible,
        attachment_visible,
        culture_visible,
        interests_visible,
        hidden_dimensions
      })

      const input = { id: partner.id, app_settings }
      const onSuccess = () => {
        success(t(I18N_PREFIX + 'success'))
        setSubmitting(false)
      }

      mutate({ input }, { onSuccess })
    }
  })

  // Standard heißt, es ist nichts gespeichert, wie bei einem Partner, der
  // nie gespeichert hat. null wie in v1, alle Leser von report_v2_settings
  // (generateText, Discover) fallen dann auf ihre Defaults zurück.
  const resetToDefault = () => {
    formik.setSubmitting(true)
    trackEvent(trackEvents.UPDATE_RESULT_REPORTS_SETTINGS_V2, { reset: true })

    const app_settings = {
      ...(partner.app_settings || {}),
      report_v2_settings: null
    }

    mutate(
      { input: { id: partner.id, app_settings } },
      {
        onSuccess: () => {
          success(t(I18N_PREFIX + 'success'))
          formik.setSubmitting(false)
        },
        onError: () => {
          error()
          formik.setSubmitting(false)
        }
      }
    )
  }

  // Ohne CUSTOM_REPORTS lassen sich eigene Einstellungen danach nicht neu
  // anlegen, deshalb mit Rückfrage
  const confirmReset = () =>
    alert(
      t('pricing.gates.reports_reset_confirm_title'),
      [t('pricing.gates.reports_reset_confirm_text')],
      [
        {
          onPress: resetToDefault,
          text: t('pricing.gates.reports_reset_action'),
          style: 'destructive'
        },
        { text: t('pricing.dialog.cancel_action'), style: 'cancel' }
      ]
    )

  const toggle_appearance = 'with-left-label-and-description'

  return (
    <>
      <ReadOnlyFieldset
        readOnly={readOnly}
        className='flex flex-col gap-y-4 bg-gray-50 px-4 py-5 sm:rounded-md sm:p-6'
      >
        {[
          {
            label: t(I18N_PREFIX + 'score.title'),
            description: t(I18N_PREFIX + 'score.description'),
            enabled: formik.values.score_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'score_visible',
                !formik.values.score_visible
              )
          },
          {
            label: t(I18N_PREFIX + 'attachment.title'),
            description: t(I18N_PREFIX + 'attachment.description'),
            enabled: formik.values.attachment_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'attachment_visible',
                !formik.values.attachment_visible
              )
          },
          {
            label: t(I18N_PREFIX + 'culture.title'),
            description: t(I18N_PREFIX + 'culture.description'),
            enabled: formik.values.culture_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'culture_visible',
                !formik.values.culture_visible
              )
          },
          {
            label: t(I18N_PREFIX + 'interests.title'),
            description: t(I18N_PREFIX + 'interests.description'),
            enabled: formik.values.interests_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'interests_visible',
                !formik.values.interests_visible
              )
          }
        ].map((toggle, index) => (
          <Fragment key={index}>
            <Toggle {...{ ...toggle, appearance: toggle_appearance }} />
            <Divider />
          </Fragment>
        ))}
        <div>
          <h4 className='text-sm font-medium leading-6 text-gray-900'>
            {t(I18N_PREFIX + 'dimensions.title')}
          </h4>
          <p className='text-sm text-gray-500'>
            {t(I18N_PREFIX + 'dimensions_visible', {
              count:
                dimensions_v2.filter(
                  ({ category }) => category === 1 || category === 2
                ).length - formik.values.hidden_dimensions.length
            })}
          </p>
          <div className='mt-4 flex flex-col gap-y-6'>
            {[
              {
                category: system.categories['1'],
                dimensions: dimensions_v2.filter(
                  ({ category }) => category === 1
                )
              },
              {
                category: system.categories['2'],
                dimensions: dimensions_v2.filter(
                  ({ category }) => category === 2
                )
              }
            ].map(({ category, dimensions }, index) => (
              <div key={`category_${index}`}>
                <h5 className='text-sm text-gray-700'>
                  {category.name[language] + ':'}
                </h5>
                <div className='mt-2 flex flex-row flex-wrap gap-2'>
                  {dimensions.map(({ key, property }, index) => {
                    const isHidden =
                      formik.values.hidden_dimensions.includes(key)

                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (readOnly) return

                          if (isHidden) {
                            formik.setFieldValue('hidden_dimensions', [
                              ...formik.values.hidden_dimensions.filter(
                                (dimension) => dimension !== key
                              )
                            ])
                          } else {
                            formik.setFieldValue('hidden_dimensions', [
                              ...formik.values.hidden_dimensions,
                              key
                            ])
                          }
                        }}
                        className={clsx(
                          'flex items-center justify-center rounded-full px-4 py-2 text-sm text-white',
                          readOnly ? 'cursor-default' : 'cursor-pointer',
                          isHidden
                            ? clsx(
                                'bg-blue-600/30',
                                !readOnly && 'hover:bg-blue-600/70'
                              )
                            : 'bg-blue-600'
                        )}
                      >
                        {property[language]}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ReadOnlyFieldset>
      <div className='mt-6 flex justify-end'>
        {readOnly ? (
          <Button.SecondaryXL
            text={t('pricing.gates.reports_reset_action')}
            onClick={confirmReset}
            isLoading={formik.isSubmitting}
          />
        ) : (
          <Button.PrimaryXL
            text={t(I18N_PREFIX + 'save_action')}
            className='bg-green-600 hover:bg-green-600/90'
            onClick={formik.handleSubmit}
            disabled={!formik.dirty}
            isLoading={formik.isSubmitting}
          />
        )}
      </div>
    </>
  )
}

SettingsV2.propTypes = { readOnly: PropTypes.bool }

SettingsV2.defaultProps = { readOnly: false }

export default SettingsV2
